import { inject, Injectable } from "@angular/core";
import { CheckoutNavigationService } from "@modules/checkout/services/checkout-navigation.service";
import {
	AmountType,
	CheckoutQueryParamName,
	CheckoutQueryParamsCollection,
	CheckoutRouteName,
	PaymentMethod
} from "@modules/checkout/entities/checkout.model";
import { TradeType } from "@gql/offer";
import { MarginTradingRouteName } from "@modules/margin-trading/entities/margin-trading.model";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { AccountService } from "@modules/account/services/account.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { Helper } from "@helpers/functions";
import { API_LEVERAGED_TOKEN_TYPE, MARGIN_TRADING_INTRO_SEEN } from "@helpers/constants";
import { CheckoutDataService } from "@modules/checkout/services/checkout-data.facade";
import { LimitOrderNavigationFacade } from "@modules/limit-orders/services/navigation.facade";
import {
	LimitOrderQueryParamName,
	LimitOrderRouteName
} from "@modules/limit-orders/interfaces/limit-orders-route.interface";

@Injectable({
	providedIn: "root"
})
export class MarginTradingEntryHelper {
	private readonly checkoutNavigation = inject(CheckoutNavigationService);
	private readonly tradableListService = inject(TradableListService);
	private readonly accountService = inject(AccountService);
	private readonly fiatWalletService = inject(FiatWalletService);
	private readonly checkoutDataService = inject(CheckoutDataService);
	private readonly limitOrderNavigation = inject(LimitOrderNavigationFacade);

	public openMarginTradingFlow(flow: TradeType, assetPid?: string): void {
		const marginIntroSeen = Helper.getCookie(MARGIN_TRADING_INTRO_SEEN) === "true";
		let tradable = this.tradableListService.getItemByPid(assetPid);
		if (!tradable) {
			tradable = this.tradableListService.getItemById(assetPid);
		}
		if (!marginIntroSeen && flow === TradeType.Buy) {
			this.checkoutNavigation.gotoByNavigation(MarginTradingRouteName.MarginTradingIntro, {
				[CheckoutQueryParamName.tradeType]: flow,
				[CheckoutQueryParamName.AssetId]: tradable.getId()
			});
			return;
		}
		if (flow === TradeType.Buy) {
			this.openMarginTradingBuy(assetPid);
		} else {
			this.openMarginTradingSell(assetPid);
		}
	}

	private openMarginTradingBuy(assetPid?: string): void {
		let queryParams: Partial<CheckoutQueryParamsCollection> = {
			tradeType: TradeType.Buy
		};
		if (assetPid) {
			const fiatId = this.accountService.account?._fiat_id;
			let tradable = this.tradableListService.getItemByPid(assetPid);
			// TODO: not sure if necessary, in case we want to support asset id p.e from ADP
			if (!tradable) {
				tradable = this.tradableListService.getItemById(assetPid);
			}
			// // wallet logic
			// let walletList = this.availableWallets(TradeType.Buy, tradable, this.fiatService.getItemById(fiatId));

			// // If we have more than 1 wallet we need to make sure that dummy wallet isn't one of them
			// if (walletList.length > 1) {
			// 	walletList = walletList.filter((w) => !w.isNew());
			// }
			const defaultWallet = this.checkoutDataService.getDefaultTradableWallet(tradable.getId());

			queryParams = {
				tradeType: TradeType.Buy,
				assetId: tradable.getId(),
				assetPid: tradable.getPid(),
				amount: 0,
				leverage: 0,
				paymentMethod: PaymentMethod.fiatwallet,
				paymentMethodId: "none",
				selectedAmountType: AmountType.fiat,
				fiatId: fiatId,
				fiatWalletId: this.fiatWalletService.getWalletByFiatId(fiatId)?._id,
				walletId: defaultWallet.getId()
			};
			this.checkoutNavigation.gotoByNavigation(MarginTradingRouteName.MarginTradingBuyAmount, queryParams);
		} else {
			// TODO: go directly to leverage pill pre-selected
			this.checkoutNavigation.gotoByNavigationUrl(CheckoutRouteName.AssetSelect, {
				selectedPill: API_LEVERAGED_TOKEN_TYPE
			});
		}
	}

	private openMarginTradingSell(assetPid?: string): void {
		if (!assetPid) {
			return;
		}
		let queryParams: Partial<CheckoutQueryParamsCollection> = {
			tradeType: TradeType.Sell
		};
		// Fiat wallet selection
		const fiatId = this.accountService.account?.getFiatId();
		let tradable = this.tradableListService.getItemByPid(assetPid);
		if (!tradable) {
			tradable = this.tradableListService.getItemById(assetPid);
		}
		const defaultWallet = this.checkoutDataService.getDefaultTradableWallet(tradable.getId());

		queryParams = {
			tradeType: TradeType.Sell,
			assetId: tradable.getId(),
			assetPid: tradable.getPid(),
			amount: 0,
			selectedAmountType: AmountType.fiat,
			fiatId,
			fiatWalletId: this.fiatWalletService.getWalletByFiatId(fiatId)?._id,
			walletId: defaultWallet.getId()
		};

		this.checkoutNavigation.gotoByNavigation(MarginTradingRouteName.MarginTradingSellAmount, queryParams);
	}

	openMarginTradingOrderDetail(orderId: string, fiatId: string, handleQueryParams = false): void {
		this.limitOrderNavigation.gotoByNavigation(
			LimitOrderRouteName.MtLimitOrderDetail,
			{
				[LimitOrderQueryParamName.OrderId]: orderId,
				[LimitOrderQueryParamName.FiatId]: fiatId
			},
			handleQueryParams
		);
	}

	createLimitOrder(assetPid: string): void {
		const tradable = this.checkoutDataService.getLegacyTradeableByPid(assetPid);
		const assetId = tradable.getId();
		const defaultFiat = this.accountService.getDefaultFiat();
		const fiatId = defaultFiat.getId();
		const fiatWallet = this.fiatWalletService.getWalletByFiatId(fiatId);
		const fiatWalletId = fiatWallet._id;
		const defaultWallet = this.checkoutDataService.getDefaultTradableWallet(assetId);
		const walletId = defaultWallet._id;

		const queryParams: Partial<CheckoutQueryParamsCollection> = {
			fiatId,
			walletId,
			assetId,
			fiatWalletId
		};

		this.checkoutNavigation.gotoByNavigation(MarginTradingRouteName.MarginLimitOrderSellAmount, queryParams);
	}
}
